import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { foldForSearch } from '@/utils/foldForSearch'

export interface MultiSelectOption {
  id: string
  label: string
  hint?: string
  caption?: string
}

interface MultiSelectFilterProps {
  id: string
  label: string
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  searchable?: boolean
}

const DROPDOWN_MAX_HEIGHT = 224
const PREFERRED_MENU_MAX_WIDTH = 512
const EDGE_MARGIN = 8

function optionMatchesSearch(opt: MultiSelectOption, searchFolded: string): boolean {
  if (foldForSearch(opt.label).includes(searchFolded)) return true
  if (opt.hint && foldForSearch(opt.hint).includes(searchFolded)) return true
  if (opt.caption && foldForSearch(opt.caption).includes(searchFolded)) return true
  if (foldForSearch(opt.id).includes(searchFolded)) return true
  return false
}

function optionFullTitle(opt: Pick<MultiSelectOption, 'label' | 'hint' | 'caption'>): string {
  return [opt.label, opt.hint, opt.caption].filter(Boolean).join(' › ')
}

export default function MultiSelectFilter({
  id,
  label,
  options,
  selected,
  onChange,
  placeholder = 'All',
  searchable = true,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [menuStyle, setMenuStyle] = useState<{
    top?: number
    bottom?: number
    left: number
    width: number
  } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const active = selected.length > 0

  const selectedSet = useMemo(() => new Set(selected), [selected])

  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options
    const q = foldForSearch(search.trim())
    return options.filter((opt) => optionMatchesSearch(opt, q))
  }, [options, search, searchable])

  const buttonLabel = useMemo(() => {
    if (selected.length === 0) return placeholder
    if (selected.length === 1) {
      return options.find((opt) => opt.id === selected[0])?.label ?? '1 selected'
    }
    return `${selected.length} selected`
  }, [options, placeholder, selected])

  const buttonTitle = useMemo(() => {
    if (selected.length !== 1) return undefined
    const opt = options.find((item) => item.id === selected[0])
    return opt ? optionFullTitle(opt) : undefined
  }, [options, selected])

  function updateMenuPosition() {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const openAbove = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow
    const preferredMenuWidth = Math.min(
      PREFERRED_MENU_MAX_WIDTH,
      window.innerWidth - EDGE_MARGIN * 2
    )
    const width = Math.max(rect.width, preferredMenuWidth)
    const left = Math.max(
      EDGE_MARGIN,
      Math.min(rect.left, window.innerWidth - width - EDGE_MARGIN)
    )

    if (openAbove) {
      setMenuStyle({
        bottom: window.innerHeight - rect.top + 4,
        left,
        width,
      })
      return
    }

    setMenuStyle({
      top: rect.bottom + 4,
      left,
      width,
    })
  }

  useLayoutEffect(() => {
    if (!open) return
    updateMenuPosition()
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleReposition() {
      updateMenuPosition()
    }
    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)
    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleClick(event: MouseEvent) {
      const target = event.target as Node
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
      setSearch('')
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  useEffect(() => {
    if (!open || !searchable) return
    const frame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [open, searchable])

  function toggleOption(optionId: string) {
    if (selectedSet.has(optionId)) {
      onChange(selected.filter((id) => id !== optionId))
      return
    }
    onChange([...selected, optionId])
  }

  function setOpenState(next: boolean) {
    setOpen(next)
    if (!next) {
      setSearch('')
      setMenuStyle(null)
    }
  }

  const dropdown = open && menuStyle ? (
    <div
      ref={menuRef}
      className="fixed z-[200] rounded-lg border border-gray-200 bg-white shadow-lg"
      style={{
        top: menuStyle.top,
        bottom: menuStyle.bottom,
        left: menuStyle.left,
        width: menuStyle.width,
      }}
    >
      {searchable ? (
        <div className="border-b border-gray-100 p-2">
          <input
            ref={searchInputRef}
            type="text"
            role="searchbox"
            className="input-sm w-full"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation()
                setOpenState(false)
              }
            }}
          />
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-2 py-1.5">
        <button
          type="button"
          className="shrink-0 whitespace-nowrap text-xs font-medium text-brand-700 hover:underline"
          onClick={() => onChange(filteredOptions.map((opt) => opt.id))}
        >
          Select all
        </button>
        <button
          type="button"
          className="shrink-0 whitespace-nowrap text-xs font-medium text-gray-600 hover:underline"
          onClick={() => onChange([])}
        >
          Clear
        </button>
      </div>
      <ul className="max-h-56 overflow-y-auto py-1">
        {filteredOptions.length === 0 ? (
          <li className="px-3 py-2 text-xs text-gray-500">No matches</li>
        ) : (
          filteredOptions.map((opt) => (
            <li key={opt.id}>
              <label className="flex cursor-pointer items-start gap-2 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0 rounded border-gray-300"
                  checked={selectedSet.has(opt.id)}
                  onChange={() => toggleOption(opt.id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block whitespace-normal break-words">{opt.label}</span>
                  {opt.hint ? (
                    <span className="mt-0.5 block text-xs text-gray-500 break-words">
                      {opt.hint}
                    </span>
                  ) : null}
                  {opt.caption ? (
                    <span className="mt-0.5 block text-xs text-gray-400 break-words">
                      {opt.caption}
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          ))
        )}
      </ul>
    </div>
  ) : null

  return (
    <div className="relative flex flex-col gap-1" ref={containerRef}>
      <label
        htmlFor={id}
        className={`block h-4 truncate text-xs font-medium leading-4 ${active ? 'text-blue-600' : 'text-gray-600'}`}
      >
        {label}
      </label>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`multi-select-filter-trigger ${active ? 'multi-select-filter-trigger--active' : ''}`}
        aria-expanded={open}
        title={buttonTitle}
        onClick={() => setOpenState(!open)}
      >
        <span className="multi-select-filter-trigger__label">{buttonLabel}</span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {dropdown ? createPortal(dropdown, document.body) : null}
    </div>
  )
}
